import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { getSupplierById, getSupplierParts, deleteSupplier } from '../../services/supplierService';
import SupplierPartGrid from '../../components/suppliers/SupplierPartGrid';

// Material UI components
import {
  Box,
  Container,
  Typography,
  Alert,
  AlertTitle,
  Button,
  Grid,
  Paper,
  Chip,
  Breadcrumbs,
  Link,
  useTheme,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar
} from '@mui/material';

// Material UI icons
import {
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Inventory as InventoryIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

const SupplierDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const [supplier, setSupplier] = useState(null);
  const [supplierParts, setSupplierParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Check for notifications from location state
  useEffect(() => {
    if (location.state?.notification) {
      setSnackbar({
        open: true,
        message: location.state.notification.message,
        severity: location.state.notification.severity || 'info'
      });

      // Clear the location state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  useEffect(() => {
    const fetchSupplierData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch supplier details
        const supplierData = await getSupplierById(id);
        setSupplier(supplierData);

        // Fetch supplier parts
        try {
          const partsData = await getSupplierParts(id);
          setSupplierParts(partsData);
        } catch (partsError) {
          console.error('Error fetching supplier parts:', partsError);
          // Don't set error state here, just log it and continue
          setSupplierParts([]);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching supplier details:', err);
        setError(err.message || 'Failed to load supplier details');
        setLoading(false);
      }
    };

    fetchSupplierData();
  }, [id]);

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await deleteSupplier(id);

      // Redirect to supplier list with success message
      navigate('/suppliers', {
        state: {
          notification: {
            message: 'Supplier deleted successfully',
            severity: 'success'
          }
        }
      });
    } catch (err) {
      console.error('Error deleting supplier:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to delete supplier',
        severity: 'error'
      });
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // If loading, show loading indicator
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // If error, show error message
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to="/suppliers"
        >
          Back to Suppliers
        </Button>
      </Container>
    );
  }

  // If supplier not found, show not found message
  if (!supplier) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning" sx={{ mb: 4 }}>
          <AlertTitle>Supplier Not Found</AlertTitle>
          The supplier you are looking for does not exist or has been deleted.
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to="/suppliers"
        >
          Back to Suppliers
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs navigation */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Link
            underline="hover"
            color="inherit"
            component={RouterLink}
            to="/"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Dashboard
          </Link>
          <Link
            underline="hover"
            color="inherit"
            component={RouterLink}
            to="/suppliers"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <BusinessIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Suppliers
          </Link>
          <Typography
            color="text.primary"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            Supplier Details
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Supplier Details
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            component={RouterLink}
            to="/suppliers"
          >
            Back to Suppliers
          </Button>
        </Box>

        {/* Supplier Information Header */}
        <Paper
          elevation={1}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            background: theme.palette.background.paper
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon
                  color="primary"
                  sx={{ fontSize: 28, mr: 1.5 }}
                />
                <Typography variant="h5" component="h2">
                  {supplier.name}
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                    <LocationIcon sx={{ color: 'text.secondary', mr: 1, mt: 0.25 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Address
                      </Typography>
                      <Typography variant="body1">
                        {supplier.address || 'Not specified'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                    <PhoneIcon sx={{ color: 'text.secondary', mr: 1, mt: 0.25 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="body1">
                        {supplier.phone || 'Not specified'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                    <EmailIcon sx={{ color: 'text.secondary', mr: 1, mt: 0.25 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1">
                        {supplier.email || 'Not specified'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                    <PersonIcon sx={{ color: 'text.secondary', mr: 1, mt: 0.25 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Contact Person
                      </Typography>
                      <Typography variant="body1">
                        {supplier.contact_person || 'Not specified'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6} sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Supplier Summary
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<InventoryIcon />}
                    label={`${supplierParts.length} Parts`}
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: { xs: 3, md: 0 } }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteClick}
                >
                  Delete
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<EditIcon />}
                  component={RouterLink}
                  to={`/suppliers/edit/${supplier.id}`}
                >
                  Edit Supplier
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Supplier Parts Grid */}
        <SupplierPartGrid
          supplierId={supplier.id}
          supplierParts={supplierParts}
          onPartsChange={() => {
            // Refresh supplier parts when changes are made
            getSupplierParts(id).then(data => setSupplierParts(data));
          }}
        />
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon color="error" sx={{ mr: 1 }} />
            Confirm Deletion
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the supplier "{supplier?.name}"? This action cannot be undone.
            {supplierParts.length > 0 && (
              <Box sx={{ mt: 2, color: 'error.main' }}>
                <Typography variant="body2" fontWeight="bold">
                  Warning: This supplier has {supplierParts.length} associated parts.
                </Typography>
                <Typography variant="body2">
                  Deleting this supplier will remove all part associations.
                </Typography>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SupplierDetails;
